/**
 * Abre o app Whizr sozinho quando um link de evento/estabelecimento é aberto no
 * navegador embutido de Instagram, Facebook, TikTok & cia. — o mesmo que o
 * `wa.me` faz com o WhatsApp.
 *
 * Esses apps abrem o link numa webview própria, e o Universal Link (iOS) / App
 * Link (Android) só dispara quando o sistema recebe o link. Então, ao carregar,
 * a página manda a webview para o esquema do app, que ela repassa ao sistema:
 *
 * - Android: `intent://`, com o próprio endereço como fallback — sem o app, o
 *   navegador recarrega a página e segue no site.
 * - iOS: `whizr://`. Sem o app a navegação só falha e o site segue normal.
 *
 * Uma vez por endereço na sessão: quem volta do app para o Instagram não é
 * jogado de volta, e o fallback do Android não entra em loop.
 *
 * Os caminhos espelham o `parseWhizrLink` do app (ENG_APP_Platform,
 * src/helpers/deepLink.js).
 */

const APP_SCHEME = 'whizr';
const ANDROID_PACKAGE = 'app.whizr.android';
const ATTEMPTED_KEY = 'open_in_app_attempted';

const IN_APP_BROWSER = /Instagram|FBAN|FBAV|FB_IAB|Threads|Barcelona|musical_ly|BytedanceWebview|TikTok|LinkedInApp|Snapchat|Pinterest/i;

function getPlatform() {
    const ua = navigator.userAgent || '';
    if (!IN_APP_BROWSER.test(ua)) return null;
    if (/Android/i.test(ua)) return 'android';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
    return null;
}

/** "/evento/x/estabelecimento/y" → "evento/x"; null se o app não tem a tela. */
function getAppPath(pathname, search) {
    const segments = pathname.split('/').filter(Boolean).map(decodeURIComponent);

    if (segments[0] === 'evento' && segments[1]) return `evento/${encodeURIComponent(segments[1])}`;
    if (segments[0] === 'estabelecimento' && segments[1]) return `estabelecimento/${encodeURIComponent(segments[1])}`;

    if (segments[0] === 'estabelecimentos') {
        const slug = new URLSearchParams(search).get('estabelecimento');
        if (slug) return `estabelecimento/${encodeURIComponent(slug)}`;
    }

    return null;
}

function buildAppUrl(platform, appPath) {
    if (platform === 'android') {
        const fallback = encodeURIComponent(window.location.href);
        return `intent://${appPath}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
    }

    return `${APP_SCHEME}://${appPath}`;
}

/** Marca a tentativa; false se já houve uma para este endereço na sessão. */
function markAttempt(appPath) {
    try {
        const attempted = JSON.parse(sessionStorage.getItem(ATTEMPTED_KEY) || '[]');
        if (attempted.includes(appPath)) return false;

        sessionStorage.setItem(ATTEMPTED_KEY, JSON.stringify([...attempted, appPath]));
        return true;
    } catch {
        // Sem sessionStorage não há como evitar o loop do fallback do Android.
        return false;
    }
}

let redirectedToApp = false;

/** true se esta carga da página já mandou a pessoa para o app. */
export function didRedirectToApp() {
    return redirectedToApp;
}

export default function openInAppFromInAppBrowser() {
    try {
        const platform = getPlatform();
        if (!platform) return;

        const appPath = getAppPath(window.location.pathname, window.location.search);
        if (!appPath || !markAttempt(appPath)) return;

        redirectedToApp = true;
        window.location.href = buildAppUrl(platform, appPath);
    } catch {
        // Qualquer falha aqui só deixa a pessoa no site.
    }
}
